import React, { useEffect, useState } from 'react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  timestamp: number;
}

interface AchievementNotificationProps {
  achievement: Achievement;
  onDismiss: (id: string) => void;
}

export const AchievementNotification: React.FC<AchievementNotificationProps> = ({ achievement, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Animar entrada
    setTimeout(() => setIsVisible(true), 100);

    // Auto-dismiss após 6 segundos
    const timer = setTimeout(() => {
      handleDismiss();
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(achievement.id);
    }, 300);
  };

  return (
    <div
      className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
        isVisible && !isLeaving
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 -translate-y-4'
      }`}
    >
      <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-2xl p-1">
        <div className="bg-white rounded-xl p-6 min-w-[320px] max-w-md">
          <div className="flex items-start gap-4">
            {/* Ícone animado */}
            <div className="relative">
              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-xl opacity-50"></div>
              <div className="relative w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <i className={`${achievement.icon} text-3xl text-white`}></i>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <i className="fa-solid fa-trophy text-yellow-500 text-sm"></i>
                <span className="text-xs font-bold text-yellow-600 uppercase tracking-wide">
                  Conquista Desbloqueada!
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">
                {achievement.name}
              </h3>
              <p className="text-sm text-gray-600">
                {achievement.description}
              </p>
            </div>

            {/* Botão fechar */}
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-times text-lg"></i>
            </button>
          </div>

          {/* Barra de progresso */}
          <div className="mt-4 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-orange-500"
              style={{
                animation: 'shrinkProgress 6s linear forwards'
              }}
            ></div>
          </div>
        </div>
      </div>

      {/* Confetes animados */}
      <div className="absolute inset-0 pointer-events-none overflow-visible">
        {[...Array(20)].map((_, i) => {
          const xOffset = (Math.random() - 0.5) * 300; // Movimento horizontal aleatório
          const rotation = Math.random() * 720 - 360;
          const colors = ['#fbbf24', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6'];
          
          return (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{
                left: '50%',
                top: '0%',
                backgroundColor: colors[i % 5],
                animation: `confettiFall 2s ease-out forwards`,
                animationDelay: `${i * 0.05}s`,
                transform: 'translate(-50%, -50%)',
                '--x-offset': `${xOffset}px`,
                '--rotation': `${rotation}deg`
              } as React.CSSProperties}
            />
          );
        })}
      </div>
    </div>
  );
};

// Container para múltiplas notificações
interface AchievementNotificationsProps {
  achievements: Achievement[];
  onDismiss: (id: string) => void;
}

export const AchievementNotifications: React.FC<AchievementNotificationsProps> = ({ achievements, onDismiss }) => {
  // Mostrar apenas a primeira notificação para evitar duplicatas
  const currentAchievement = achievements[0];
  
  if (!currentAchievement) return null;
  
  return (
    <AchievementNotification
      achievement={currentAchievement}
      onDismiss={onDismiss}
    />
  );
};